import {
  type ImplantReference,
  type ReferencedImplantCharacteristics,
} from "@jasper/client-models";
import { Group, Select } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { FieldValueProps } from "../../types";
import { useState } from "react";

interface Props extends FieldValueProps<ReferencedImplantCharacteristics> {
  implantReferences: ImplantReference[];
  implantReferencesAreLoading?: boolean;
}

export const ReferencedImplantCharacteristicsForm = ({
  value,
  errors,
  onChange,
  implantReferences,
  implantReferencesAreLoading,
}: Props) => {
  const { t } = useTranslation("forms", {
    keyPrefix: "forms.implantCharacteristics",
  });

  const currentReference = value?.implantReferenceId
    ? implantReferences.find(ref => ref.id === value.implantReferenceId)
    : undefined;

  const [draftBrand, setDraftBrand] = useState<string | undefined>(
    value?.implantReferenceId ? currentReference?.brand : undefined
  );

  const currentBrand = currentReference?.brand ?? draftBrand;

  const brands = [...new Set(implantReferences.map(ref => ref.brand))];
  const referencesChoices = currentBrand
    ? implantReferences.filter(ref => ref.brand === currentBrand)
    : [];

  const handleBrandChange = (nextBrand?: string | null) => {
    onChange({
      ...value,
      implantReferenceId: undefined,
      implantPlatform: undefined,
    });
    setDraftBrand(nextBrand ?? undefined);
  };

  const handleReferenceChange = (nextReferenceId?: string | null) => {
    onChange({
      ...value,
      implantReferenceId: nextReferenceId ?? undefined,
      implantPlatform: undefined,
    });
  };

  return (
    <Group align="flex-start">
      <Select
        flex={1}
        label={t("labels.implantBrand")}
        placeholder={t("placeholders.implantBrand")}
        data={brands.map(brand => ({ value: brand, label: brand }))}
        loading={implantReferencesAreLoading}
        value={currentBrand ?? null}
        onChange={handleBrandChange}
        error={
          errors?.implantReferenceId && !currentBrand
            ? t("errors.implantBrand")
            : undefined
        }
      />
      <Select
        flex={1}
        label={t("labels.implantSystem")}
        placeholder={t("placeholders.implantSystem")}
        data={referencesChoices.map(ref => ({
          value: ref.id,
          label: ref.reference,
        }))}
        value={currentReference?.id ?? null}
        disabled={!currentBrand}
        onChange={handleReferenceChange}
        error={
          errors?.implantReferenceId && !!currentBrand
            ? t("errors.implantSystem")
            : undefined
        }
      />
      <Select
        flex={1}
        label={t("labels.implantPlatform")}
        placeholder={t("placeholders.implantPlatform")}
        data={currentReference?.platforms.map(platform => ({
          value: platform,
          label: platform,
        }))}
        value={value?.implantPlatform ?? null}
        disabled={!currentReference}
        onChange={next =>
          onChange({ ...value, implantPlatform: next ?? undefined })
        }
        error={
          errors?.implantPlatform && !!currentReference
            ? t("errors.implantPlatform")
            : undefined
        }
      />
    </Group>
  );
};
